// Dependency-free helpers for a small cluster of generator/calculator tools.
// Everything here runs locally in the browser: MD5 hashing, plain date math,
// text repetition, and a safe scientific-expression evaluator (tokenizer +
// recursive-descent parser, no eval/new Function).

/* -------------------------------------------------------------------------- */
/* MD5                                                                         */
/* -------------------------------------------------------------------------- */

// Pure MD5 implementation (RFC 1321). MD5 is a fast checksum, not a secure
// hash: never use it for passwords or signatures.

function leftRotate(value: number, amount: number): number {
  return (value << amount) | (value >>> (32 - amount));
}

function toUtf8Bytes(input: string): number[] {
  const bytes: number[] = [];
  for (let i = 0; i < input.length; i += 1) {
    let codePoint = input.charCodeAt(i);
    if (codePoint >= 0xd800 && codePoint <= 0xdbff && i + 1 < input.length) {
      const next = input.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        codePoint = 0x10000 + ((codePoint - 0xd800) << 10) + (next - 0xdc00);
        i += 1;
      }
    }
    if (codePoint < 0x80) {
      bytes.push(codePoint);
    } else if (codePoint < 0x800) {
      bytes.push(0xc0 | (codePoint >> 6), 0x80 | (codePoint & 0x3f));
    } else if (codePoint < 0x10000) {
      bytes.push(
        0xe0 | (codePoint >> 12),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    } else {
      bytes.push(
        0xf0 | (codePoint >> 18),
        0x80 | ((codePoint >> 12) & 0x3f),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    }
  }
  return bytes;
}

const MD5_SHIFTS = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14,
  20, 5, 9, 14, 20, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6,
  10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
];

const MD5_CONSTANTS = (() => {
  const table: number[] = [];
  for (let i = 0; i < 64; i += 1) {
    table.push(Math.floor(Math.abs(Math.sin(i + 1)) * 0x100000000));
  }
  return table;
})();

export function md5(input: string): string {
  const bytes = toUtf8Bytes(input);
  const originalLengthBits = bytes.length * 8;

  bytes.push(0x80);
  while (bytes.length % 64 !== 56) {
    bytes.push(0);
  }

  // Append original length as 64-bit little-endian.
  const lengthLow = originalLengthBits >>> 0;
  const lengthHigh = Math.floor(originalLengthBits / 0x100000000) >>> 0;
  for (let i = 0; i < 4; i += 1) {
    bytes.push((lengthLow >>> (8 * i)) & 0xff);
  }
  for (let i = 0; i < 4; i += 1) {
    bytes.push((lengthHigh >>> (8 * i)) & 0xff);
  }

  let a0 = 0x67452301;
  let b0 = 0xefcdab89;
  let c0 = 0x98badcfe;
  let d0 = 0x10325476;

  for (let chunk = 0; chunk < bytes.length; chunk += 64) {
    const words = new Array<number>(16);
    for (let i = 0; i < 16; i += 1) {
      const j = chunk + i * 4;
      words[i] = bytes[j] | (bytes[j + 1] << 8) | (bytes[j + 2] << 16) | (bytes[j + 3] << 24);
    }

    let a = a0;
    let b = b0;
    let c = c0;
    let d = d0;

    for (let i = 0; i < 64; i += 1) {
      let f: number;
      let g: number;
      if (i < 16) {
        f = (b & c) | (~b & d);
        g = i;
      } else if (i < 32) {
        f = (d & b) | (~d & c);
        g = (5 * i + 1) % 16;
      } else if (i < 48) {
        f = b ^ c ^ d;
        g = (3 * i + 5) % 16;
      } else {
        f = c ^ (b | ~d);
        g = (7 * i) % 16;
      }

      const sum = (a + f + MD5_CONSTANTS[i] + words[g]) | 0;
      a = d;
      d = c;
      c = b;
      b = (b + leftRotate(sum, MD5_SHIFTS[i])) | 0;
    }

    a0 = (a0 + a) | 0;
    b0 = (b0 + b) | 0;
    c0 = (c0 + c) | 0;
    d0 = (d0 + d) | 0;
  }

  return [a0, b0, c0, d0].map(wordToHexLE).join('');
}

function wordToHexLE(word: number): string {
  let hex = '';
  for (let i = 0; i < 4; i += 1) {
    const byte = (word >>> (8 * i)) & 0xff;
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex;
}

/* -------------------------------------------------------------------------- */
/* Text repeater                                                               */
/* -------------------------------------------------------------------------- */

export type RepeatSeparator = 'newline' | 'space' | 'comma' | 'none' | 'custom';

export interface RepeatOptions {
  text: string;
  count: number;
  separator: RepeatSeparator;
  customSeparator: string;
  numbered: boolean;
  trim: boolean;
}

export function resolveSeparator(separator: RepeatSeparator, custom: string): string {
  switch (separator) {
    case 'newline':
      return '\n';
    case 'space':
      return ' ';
    case 'comma':
      return ', ';
    case 'none':
      return '';
    case 'custom':
      return custom;
    default:
      return '\n';
  }
}

export function repeatText(options: RepeatOptions): string {
  const { text, count, separator, customSeparator, numbered, trim } = options;
  const safeCount = Number.isFinite(count) ? Math.max(0, Math.min(Math.floor(count), 100000)) : 0;
  if (safeCount === 0 || text.length === 0) {
    return '';
  }
  const base = trim ? text.trim() : text;
  const glue = resolveSeparator(separator, customSeparator);
  const parts: string[] = [];
  for (let i = 0; i < safeCount; i += 1) {
    parts.push(numbered ? `${i + 1}. ${base}` : base);
  }
  return parts.join(glue);
}

export function countLines(value: string): number {
  if (value.length === 0) {
    return 0;
  }
  return value.split('\n').length;
}

/* -------------------------------------------------------------------------- */
/* Age calculator                                                             */
/* -------------------------------------------------------------------------- */

export interface AgeBreakdown {
  years: number;
  months: number;
  days: number;
  totalDays: number;
  totalWeeks: number;
  totalMonths: number;
  totalHours: number;
  nextBirthdayInDays: number;
  nextBirthdayDate: string;
  weekdayBorn: string;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

// Parses a yyyy-mm-dd string into a local Date at midnight, or null.
export function parseDateInput(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

export function calculateAge(birth: Date, asOf: Date): AgeBreakdown | null {
  if (asOf.getTime() < birth.getTime()) {
    return null;
  }

  let years = asOf.getFullYear() - birth.getFullYear();
  let months = asOf.getMonth() - birth.getMonth();
  let days = asOf.getDate() - birth.getDate();

  if (days < 0) {
    months -= 1;
    const borrowMonth = asOf.getMonth() - 1;
    const borrowYear = borrowMonth < 0 ? asOf.getFullYear() - 1 : asOf.getFullYear();
    const normalizedMonth = (borrowMonth + 12) % 12;
    days += daysInMonth(borrowYear, normalizedMonth);
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const msPerDay = 1000 * 60 * 60 * 24;
  const totalDays = Math.floor((asOf.getTime() - birth.getTime()) / msPerDay);
  const totalWeeks = Math.floor(totalDays / 7);
  const totalMonths = years * 12 + months;
  const totalHours = totalDays * 24;

  // Next birthday relative to asOf.
  let nextBirthday = new Date(asOf.getFullYear(), birth.getMonth(), birth.getDate());
  if (nextBirthday.getTime() <= asOf.getTime()) {
    nextBirthday = new Date(asOf.getFullYear() + 1, birth.getMonth(), birth.getDate());
  }
  const nextBirthdayInDays = Math.ceil((nextBirthday.getTime() - asOf.getTime()) / msPerDay);
  const nextBirthdayDate = formatLongDate(nextBirthday);

  return {
    years,
    months,
    days,
    totalDays,
    totalWeeks,
    totalMonths,
    totalHours,
    nextBirthdayInDays,
    nextBirthdayDate,
    weekdayBorn: WEEKDAYS[birth.getDay()],
  };
}

function formatLongDate(date: Date): string {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export function todayInputValue(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/* -------------------------------------------------------------------------- */
/* Scientific calculator (safe expression evaluation)                          */
/* -------------------------------------------------------------------------- */

export type AngleMode = 'deg' | 'rad';

type TokenType = 'number' | 'operator' | 'lparen' | 'rparen' | 'function' | 'constant';

interface Token {
  type: TokenType;
  value: string;
}

const FUNCTIONS = new Set([
  'sin',
  'cos',
  'tan',
  'asin',
  'acos',
  'atan',
  'log',
  'ln',
  'sqrt',
  'abs',
  'exp',
]);

const CONSTANTS: Record<string, number> = {
  pi: Math.PI,
  e: Math.E,
};

export class ExpressionError extends Error {}

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const src = input.replace(/×/g, '*').replace(/÷/g, '/').replace(/−/g, '-');

  while (i < src.length) {
    const char = src[i];

    if (char === ' ' || char === '\t' || char === '\n') {
      i += 1;
      continue;
    }

    if ((char >= '0' && char <= '9') || char === '.') {
      let numberText = '';
      while (i < src.length && ((src[i] >= '0' && src[i] <= '9') || src[i] === '.')) {
        numberText += src[i];
        i += 1;
      }
      // Scientific notation: 1e3, 2.5e-4
      if (i < src.length && (src[i] === 'e' || src[i] === 'E')) {
        const save = i;
        let expText = src[i];
        i += 1;
        if (i < src.length && (src[i] === '+' || src[i] === '-')) {
          expText += src[i];
          i += 1;
        }
        if (i < src.length && src[i] >= '0' && src[i] <= '9') {
          while (i < src.length && src[i] >= '0' && src[i] <= '9') {
            expText += src[i];
            i += 1;
          }
          numberText += expText;
        } else {
          i = save;
        }
      }
      if ((numberText.match(/\./g) ?? []).length > 1) {
        throw new ExpressionError(`Invalid number "${numberText}"`);
      }
      tokens.push({ type: 'number', value: numberText });
      continue;
    }

    if ((char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z')) {
      let word = '';
      while (
        i < src.length &&
        ((src[i] >= 'a' && src[i] <= 'z') || (src[i] >= 'A' && src[i] <= 'Z'))
      ) {
        word += src[i];
        i += 1;
      }
      const lower = word.toLowerCase();
      if (FUNCTIONS.has(lower)) {
        tokens.push({ type: 'function', value: lower });
      } else if (lower in CONSTANTS) {
        tokens.push({ type: 'constant', value: lower });
      } else {
        throw new ExpressionError(`Unknown name "${word}"`);
      }
      continue;
    }

    if ('+-*/^%!'.includes(char)) {
      tokens.push({ type: 'operator', value: char });
      i += 1;
      continue;
    }

    if (char === '(') {
      tokens.push({ type: 'lparen', value: char });
      i += 1;
      continue;
    }

    if (char === ')') {
      tokens.push({ type: 'rparen', value: char });
      i += 1;
      continue;
    }

    throw new ExpressionError(`Unexpected character "${char}"`);
  }

  return tokens;
}

// Recursive-descent parser/evaluator.
// Grammar (lowest to highest precedence):
//   expr    := term (('+' | '-') term)*
//   term    := factor (('*' | '/' | '%') factor)*
//   factor  := unary ('^' factor)?          (right associative)
//   unary   := ('+' | '-') unary | postfix
//   postfix := primary ('!')*
//   primary := number | constant | function '(' expr ')' | '(' expr ')'

class Parser {
  private pos = 0;

  constructor(
    private readonly tokens: Token[],
    private readonly angleMode: AngleMode,
  ) {}

  parse(): number {
    const value = this.parseExpr();
    if (this.pos < this.tokens.length) {
      throw new ExpressionError('Unexpected trailing input');
    }
    return value;
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  private next(): Token {
    const token = this.tokens[this.pos];
    if (!token) {
      throw new ExpressionError('Unexpected end of expression');
    }
    this.pos += 1;
    return token;
  }

  private parseExpr(): number {
    let value = this.parseTerm();
    while (this.peek()?.type === 'operator' && '+-'.includes(this.peek()!.value)) {
      const op = this.next().value;
      const right = this.parseTerm();
      value = op === '+' ? value + right : value - right;
    }
    return value;
  }

  private parseTerm(): number {
    let value = this.parseFactor();
    while (this.peek()?.type === 'operator' && '*/%'.includes(this.peek()!.value)) {
      const op = this.next().value;
      const right = this.parseFactor();
      if (op === '*') {
        value *= right;
      } else if (op === '/') {
        if (right === 0) {
          throw new ExpressionError('Division by zero');
        }
        value /= right;
      } else {
        if (right === 0) {
          throw new ExpressionError('Modulo by zero');
        }
        value %= right;
      }
    }
    return value;
  }

  private parseFactor(): number {
    const base = this.parseUnary();
    if (this.peek()?.type === 'operator' && this.peek()!.value === '^') {
      this.next();
      const exponent = this.parseFactor();
      return Math.pow(base, exponent);
    }
    return base;
  }

  private parseUnary(): number {
    const token = this.peek();
    if (token?.type === 'operator' && (token.value === '+' || token.value === '-')) {
      this.next();
      const value = this.parseUnary();
      return token.value === '-' ? -value : value;
    }
    return this.parsePostfix();
  }

  private parsePostfix(): number {
    let value = this.parsePrimary();
    while (this.peek()?.type === 'operator' && this.peek()!.value === '!') {
      this.next();
      value = factorial(value);
    }
    return value;
  }

  private parsePrimary(): number {
    const token = this.next();

    if (token.type === 'number') {
      return Number(token.value);
    }

    if (token.type === 'constant') {
      return CONSTANTS[token.value];
    }

    if (token.type === 'function') {
      const open = this.next();
      if (open.type !== 'lparen') {
        throw new ExpressionError(`Expected "(" after ${token.value}`);
      }
      const arg = this.parseExpr();
      const close = this.next();
      if (close.type !== 'rparen') {
        throw new ExpressionError('Missing closing parenthesis');
      }
      return this.applyFunction(token.value, arg);
    }

    if (token.type === 'lparen') {
      const value = this.parseExpr();
      const close = this.next();
      if (close.type !== 'rparen') {
        throw new ExpressionError('Missing closing parenthesis');
      }
      return value;
    }

    throw new ExpressionError(`Unexpected token "${token.value}"`);
  }

  private applyFunction(name: string, arg: number): number {
    const toRad = (value: number) => (this.angleMode === 'deg' ? (value * Math.PI) / 180 : value);
    const fromResult = (value: number) => value;
    switch (name) {
      case 'sin':
        return Math.sin(toRad(arg));
      case 'cos':
        return Math.cos(toRad(arg));
      case 'tan':
        return Math.tan(toRad(arg));
      case 'asin': {
        const result = Math.asin(arg);
        return this.angleMode === 'deg' ? (result * 180) / Math.PI : result;
      }
      case 'acos': {
        const result = Math.acos(arg);
        return this.angleMode === 'deg' ? (result * 180) / Math.PI : result;
      }
      case 'atan': {
        const result = Math.atan(arg);
        return this.angleMode === 'deg' ? (result * 180) / Math.PI : result;
      }
      case 'log':
        return Math.log10(arg);
      case 'ln':
        return Math.log(arg);
      case 'sqrt':
        if (arg < 0) {
          throw new ExpressionError('Cannot take the square root of a negative number');
        }
        return Math.sqrt(arg);
      case 'abs':
        return Math.abs(arg);
      case 'exp':
        return fromResult(Math.exp(arg));
      default:
        throw new ExpressionError(`Unknown function "${name}"`);
    }
  }
}

function factorial(value: number): number {
  if (!Number.isInteger(value) || value < 0) {
    throw new ExpressionError('Factorial requires a non-negative integer');
  }
  if (value > 170) {
    throw new ExpressionError('Factorial is too large to compute');
  }
  let result = 1;
  for (let i = 2; i <= value; i += 1) {
    result *= i;
  }
  return result;
}

export interface EvaluationResult {
  ok: boolean;
  value?: number;
  formatted?: string;
  error?: string;
}

export function evaluateExpression(expression: string, angleMode: AngleMode): EvaluationResult {
  const trimmed = expression.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: '' };
  }
  try {
    const tokens = tokenize(trimmed);
    if (tokens.length === 0) {
      return { ok: false, error: '' };
    }
    const value = new Parser(tokens, angleMode).parse();
    if (!Number.isFinite(value)) {
      return { ok: false, error: 'Result is not a finite number' };
    }
    return { ok: true, value, formatted: formatNumber(value) };
  } catch (error) {
    if (error instanceof ExpressionError) {
      return { ok: false, error: error.message || 'Invalid expression' };
    }
    return { ok: false, error: 'Invalid expression' };
  }
}

export function formatNumber(value: number): string {
  if (Number.isInteger(value) && Math.abs(value) < 1e15) {
    return String(value);
  }
  const rounded = Number(value.toPrecision(12));
  return String(rounded);
}
